import {
  TrendingUp,
  TrendingDown,
  Minus,
  Star,
  Clock,
  Package,
  CheckCircle,
  AlertTriangle,
  Award,
} from 'lucide-react';
import {
  Card, CardContent, CardHeader, CardTitle 
} from '@/components/ui/card';

type MetricStatus = 'good' | 'warning' | 'poor';
type TrendDirection = 'up' | 'down' | 'neutral';

interface MetricCardProps {
  readonly title: string;
  readonly value: string | number;
  readonly target?: string | number;
  readonly trend?: TrendDirection;
  readonly trendValue?: string;
  readonly status?: MetricStatus;
}

function getTrendIcon(trend: TrendDirection) {
  switch (trend) {
    case 'up':
      return TrendingUp;
    case 'down':
      return TrendingDown;
    default:
      return Minus;
  }
}

function getTrendColor(trend: TrendDirection): string {
  switch (trend) {
    case 'up':
      return 'text-green-600';
    case 'down':
      return 'text-red-600';
    default:
      return 'text-muted-foreground';
  }
}

function MetricCard({
  title,
  value,
  target,
  trend,
  trendValue,
  status = 'good',
}: MetricCardProps) {
  const statusColors: Record<MetricStatus, string> = {
    good: 'text-green-600',
    warning: 'text-amber-600',
    poor: 'text-red-600',
  };

  const TrendIcon = trend ? getTrendIcon(trend) : Minus;
  const trendColor = trend ? getTrendColor(trend) : 'text-muted-foreground';

  return (
    <Card>
      <CardContent className="p-4">
        <p className="text-sm text-muted-foreground">{title}</p>
        <div className="flex items-end justify-between mt-2">
          <p className={`text-2xl font-bold ${statusColors[status]}`}>{value}</p>
          {trend && trendValue && (
            <div className={`flex items-center gap-1 text-sm ${trendColor}`}>
              <TrendIcon className="h-4 w-4" />
              <span>{trendValue}</span>
            </div>
          )}
        </div>
        {target && (
          <p className="text-xs text-muted-foreground mt-1">Target: {target}</p>
        )}
      </CardContent>
    </Card>
  );
}

// Mock scorecard data
const scorecardData = {
  overallScore: 87,
  overallRating: 'A',
  metrics: {
    onTimeDelivery: {
      value: 94,
      target: 95,
      trend: 'up' as const,
      trendValue: '+2%',
    },
    orderAccuracy: {
      value: 98,
      target: 98,
      trend: 'neutral' as const,
      trendValue: '0%',
    },
    qualityScore: {
      value: 92,
      target: 90,
      trend: 'up' as const,
      trendValue: '+3%',
    },
    responseTime: {
      value: '4.2h',
      target: '6h',
      trend: 'down' as const,
      trendValue: '-1.5h',
    },
    fillRate: {
      value: 96,
      target: 95,
      trend: 'up' as const,
      trendValue: '+1%',
    },
    returnRate: {
      value: 1.2,
      target: 2,
      trend: 'down' as const,
      trendValue: '-0.3%',
    },
  },
  recentPerformance: [
    {
      month: 'January 2026',
      score: 87,
      deliveries: 45,
      issues: 2 
    },
    {
      month: 'December 2025',
      score: 85,
      deliveries: 52,
      issues: 4 
    },
    {
      month: 'November 2025',
      score: 84,
      deliveries: 48,
      issues: 3 
    },
    {
      month: 'October 2025',
      score: 82,
      deliveries: 41,
      issues: 5 
    },
  ],
};

function getScoreStatusBgColor(status: MetricStatus): string {
  switch (status) {
    case 'good':
      return 'bg-green-100';
    case 'warning':
      return 'bg-amber-100';
    default:
      return 'bg-red-100';
  }
}

function getScoreStatusTextColor(status: MetricStatus): string {
  switch (status) {
    case 'good':
      return 'text-green-600';
    case 'warning':
      return 'text-amber-600';
    default:
      return 'text-red-600';
  }
}

function getScoreStatus(score: number): MetricStatus {
  if (score >= 90) return 'good';
  if (score >= 75) return 'warning';
  return 'poor';
}

function getMetricStatus(value: number, target: number, higherIsBetter = true): MetricStatus {
  if (higherIsBetter) {
    if (value >= target) return 'good';
    if (value >= target * 0.9) return 'warning';
    return 'poor';
  }
  if (value <= target) return 'good';
  if (value <= target * 1.1) return 'warning';
  return 'poor';
}

export function ScorecardPage() {
  const { metrics } = scorecardData;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Performance Scorecard</h1>
        <p className="text-muted-foreground">
          Track your supplier performance metrics and ratings
        </p>
      </div>

      {/* Overall Score */}
      <Card className="bg-gradient-to-r from-primary/10 to-primary/5">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Overall Performance Score</p>
              <div className="flex items-baseline gap-3 mt-2">
                <span className="text-5xl font-bold text-primary">
                  {scorecardData.overallScore}
                </span>
                <span className="text-2xl text-muted-foreground">/100</span>
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                Rating: <span className="font-semibold text-primary">{scorecardData.overallRating}</span>
              </p>
            </div>
            <div className="flex items-center justify-center h-24 w-24 rounded-full bg-primary/20">
              <Award className="h-12 w-12 text-primary" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Key Metrics */}
      <div>
        <h2 className="text-lg font-semibold mb-4">Key Performance Indicators</h2>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <MetricCard
            title="On-Time Delivery Rate"
            value={`${metrics.onTimeDelivery.value}%`}
            target={`${metrics.onTimeDelivery.target}%`}
            trend={metrics.onTimeDelivery.trend}
            trendValue={metrics.onTimeDelivery.trendValue}
            status={getMetricStatus(metrics.onTimeDelivery.value, metrics.onTimeDelivery.target)}
          />
          <MetricCard
            title="Order Accuracy"
            value={`${metrics.orderAccuracy.value}%`}
            target={`${metrics.orderAccuracy.target}%`}
            trend={metrics.orderAccuracy.trend}
            trendValue={metrics.orderAccuracy.trendValue}
            status={getMetricStatus(metrics.orderAccuracy.value, metrics.orderAccuracy.target)}
          />
          <MetricCard
            title="Quality Score"
            value={`${metrics.qualityScore.value}%`}
            target={`${metrics.qualityScore.target}%`}
            trend={metrics.qualityScore.trend}
            trendValue={metrics.qualityScore.trendValue}
            status={getMetricStatus(metrics.qualityScore.value, metrics.qualityScore.target)}
          />
          <MetricCard
            title="Avg Response Time"
            value={metrics.responseTime.value}
            target={metrics.responseTime.target}
            trend={metrics.responseTime.trend}
            trendValue={metrics.responseTime.trendValue}
            status="good"
          />
          <MetricCard
            title="Fill Rate"
            value={`${metrics.fillRate.value}%`}
            target={`${metrics.fillRate.target}%`}
            trend={metrics.fillRate.trend}
            trendValue={metrics.fillRate.trendValue}
            status={getMetricStatus(metrics.fillRate.value, metrics.fillRate.target)}
          />
          <MetricCard
            title="Return Rate"
            value={`${metrics.returnRate.value}%`}
            target={`${metrics.returnRate.target}%`}
            trend={metrics.returnRate.trend}
            trendValue={metrics.returnRate.trendValue}
            status={getMetricStatus(metrics.returnRate.value, metrics.returnRate.target, false)}
          />
        </div>
      </div>

      {/* Performance History */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Recent Performance History</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y">
            {scorecardData.recentPerformance.map((period) => {
              const status = getScoreStatus(period.score);
              const bgColor = getScoreStatusBgColor(status);
              const textColor = getScoreStatusTextColor(status);

              return (
                <div
                  key={period.month}
                  className="flex items-center justify-between p-4"
                >
                  <div className="flex items-center gap-4">
                    <div className={`rounded-full p-2 ${bgColor}`}>
                      <Star className={`h-5 w-5 ${textColor}`} />
                    </div>
                    <div>
                      <p className="font-medium">{period.month}</p>
                      <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Package className="h-3 w-3" />
                          {period.deliveries} deliveries
                        </span>
                        <span className="flex items-center gap-1">
                          {period.issues === 0 ? (
                            <CheckCircle className="h-3 w-3 text-green-500" />
                          ) : (
                            <AlertTriangle className="h-3 w-3 text-amber-500" />
                          )}
                          {period.issues} issues
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`text-xl font-bold ${textColor}`}>
                      {period.score}
                    </p>
                    <p className="text-xs text-muted-foreground">Score</p>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Performance Tips */}
      <Card className="border-blue-200 bg-blue-50/50">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Clock className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <p className="font-medium text-blue-800">Performance Tip</p>
              <p className="text-sm text-blue-700 mt-1">
                Your on-time delivery rate is slightly below target. Consider reviewing your logistics 
                planning to improve delivery timing and boost your overall score.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
